import { useUserContext } from "../../Hooks/useUserContext";
import { useMetricConversionService } from "../../Hooks/useMetricConversionService";
import { RouterLink } from "../../Shared/RouterLink";
import React, { useState, useEffect } from "react";
import {
	Grid,
	Typography,
	TextField,
	Button,
	Paper,
	Snackbar,
	SnackbarContent,
	Tooltip
} from "@material-ui/core";
import NotLoggedInIcon from "@material-ui/icons/MoodBad";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import ConfirmIcon from "@material-ui/icons/CheckCircle";
import { AutoResizeTextarea } from "../../Shared/AutoResizeTextarea";
import green from "@material-ui/core/colors/green";

export function EditMetricConversion(props) {
	const user = useUserContext(props.config);
	const service = useMetricConversionService(props.config);
	const isCreateMode = props.match.params.metricConversionId === undefined;
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [toastOpen, setToastOpen] = useState(false);
	const [item, setItem] = useState({
		metricConversionId: 0,
		inputUnit: "",
		rules: "",
		conversionEquation: "",
		outputUnit: "",
		notes: ""
	});

	const hasPermission = user?.isMetricConversionAdmin ?? false;

	useEffect(() => {
		const title = isCreateMode ? "Create Metric Conversion" : "Edit Metric Conversion";
		document.title = `${title} - ${props.config.appName}`;

		if (!hasPermission)
			return;

		if (isCreateMode) {
			setIsLoading(false);
		} else {
			const id = parseInt(props.match.params.metricConversionId);
			service.getById(id, (response) => {
				if (response && response.status === 200 && response.data) {
					setItem(response.data);
				}
				setIsLoading(false);
			}, (error) => {
				console.error(error);
				if (error.response && error.response.status === 404) {
					props.history.push("/notfound");
				} else {
					alert("Failed to load metric conversion");
				}
				setIsLoading(false);
			});
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function handleChange(event) {
		const { name, value } = event.target;
		setItem({
			...item,
			[name]: value
		});
	}

	function handleSave() {
		setIsSaving(true);

		const saveFunction = isCreateMode ? service.create : service.update;
		const saveArgs = isCreateMode ? [item] : [item.metricConversionId, item];

		saveFunction(...saveArgs, (response) => {
			if (response && response.status === 200) {
				setToastOpen(true);
				setTimeout(() => {
					props.history.push("/admin/metric-conversions");
				}, 1000);
			} else {
				alert("Failed to save metric conversion");
			}
			setIsSaving(false);
		}, (error) => {
			console.error(error);
			alert("Failed to save metric conversion: " + (error.response?.data || error.message));
			setIsSaving(false);
		});
	}

	if (!hasPermission)
		return (
			<Grid container spacing={8}>
				<Grid item xs={12}>
					<NotLoggedInIcon fontSize="large" />
					<Typography variant="subtitle1">
						Sorry, you must {user.isLoggedIn ? "" : "be logged in and "}have metric conversion admin permissions to view this page!
					</Typography>
				</Grid>
			</Grid>
		);

	if (isLoading)
		return <Typography style={{ padding: 20 }}>Loading...</Typography>;

	return (
		<Grid container spacing={3} style={{ padding: 20 }}>
			<Grid item xs={12}>
				<Typography variant="h4" gutterBottom>
					{isCreateMode ? "Create Metric Conversion" : "Edit Metric Conversion"}
				</Typography>
			</Grid>
			<Grid item xs={12}>
				<Paper style={{ padding: 20 }}>
					<Grid container spacing={3}>
						{/* Input Unit */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Input Unit:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="inputUnit"
									value={item.inputUnit}
									onChange={handleChange}
									required
								/>
							</Grid>
						</Grid>
						{/* Rules */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Rules (pipe-delimited):</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Tooltip title={`Example: {{value}} >= 1000|{{value}} % 2 = 0`} placement="top-start">
									<TextField
										fullWidth
										name="rules"
										value={item.rules ?? ""}
										onChange={handleChange}
									/>
								</Tooltip>
							</Grid>
						</Grid>
						{/* Conversion Equation */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Conversion Equation:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Tooltip title={`Example: {{value}} * 236.588`} placement="top-start">
									<TextField
										fullWidth
										name="conversionEquation"
										value={item.conversionEquation}
										onChange={handleChange}
										required
									/>
								</Tooltip>
							</Grid>
						</Grid>
						{/* Output Unit */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Output Unit:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="outputUnit"
									value={item.outputUnit}
									onChange={handleChange}
									required
								/>
							</Grid>
						</Grid>
						{/* Notes */}
						<Grid item xs={12} container alignItems="flex-start" spacing={2}>
							<Grid item xs={12} md={3} lg={2} style={{ alignSelf: "flex-start" }}>
								<Typography>Notes:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="notes"
									value={item.notes ?? ""}
									onChange={handleChange}
									multiline
									rows={1}
									InputProps={{
										inputComponent: AutoResizeTextarea
									}}
								/>
							</Grid>
						</Grid>
						<Grid item xs={12} style={{ marginTop: 16 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSave}
								disabled={isSaving}
								style={{ marginRight: 10 }}
							>
								<SaveIcon style={{ marginRight: 10 }} />
								{isSaving ? "Saving..." : "Save"}
							</Button>
							<Button
								variant="outlined"
								component={RouterLink}
								to="/admin/metric-conversions"
								disabled={isSaving}
							>
								<CancelIcon style={{ marginRight: 10 }} />
								Cancel
							</Button>
						</Grid>
					</Grid>
				</Paper>
			</Grid>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={toastOpen}
				autoHideDuration={3000}
				onClose={() => setToastOpen(false)}
				style={{ marginTop: 20 }}
			>
				<SnackbarContent
					style={{ backgroundColor: green[600] }}
					message={
						<div style={{ display: "flex", alignItems: "center" }}>
							<ConfirmIcon />
							<span style={{ paddingLeft: 10 }}>Metric conversion saved successfully!</span>
						</div>
					}
				/>
			</Snackbar>
		</Grid>
	);
}
